import React, { Component } from "react";
import { DragSource } from "react-dnd";
import { useTranslation } from "react-i18next";
import AddIcon from "@material-ui/icons/Add";
import _ from "lodash";
import { translateIncomeTreeData } from "../treeUtils";
import MiniRuleTree, { collectUnknownBoxTypes, countTreeNodes, getBoxCatalogEntry } from "./MiniRuleTree";

const nodeGeneralTypes = "GeneralNode"; //////////IMPORTANT

const generatedNodeSpec = {
    beginDrag: (componentProps) => ({ node: componentProps.buildIncomeNode() }),
};
const generatedNodeCollect = (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
});

class draggableGeneratedRootBase extends Component {
    render() {
        const { connectDragSource, isDragging, children } = this.props;
        return connectDragSource(
            <div className={"qia-artifact-root" + (isDragging ? " qia-artifact-root-dragging" : "")}>
                {children}
            </div>,
            { dropEffect: "copy" }
        );
    }
}

// Drag source of the same dndType as the rule tree, so react-sortable-tree
// accepts the drop exactly like a box dragged from the library.
const DraggableGeneratedRoot = DragSource(
    nodeGeneralTypes,
    generatedNodeSpec,
    generatedNodeCollect
)(draggableGeneratedRootBase);

// Box validators and configuration components assume every key of the
// catalog's default properties/metadata exists on the node. Generated nodes
// only carry the keys the agent emitted, so fill the gaps from the catalog
// before translating them into the tree. A key the agent set to null falls
// back to the catalog default when one exists.
function mergeWithDefaults(defaults, provided) {
    let merged = _.cloneDeep(defaults || {});
    Object.entries(provided || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            merged[key] = value;
        } else if (!(key in merged)) {
            merged[key] = value;
        }
    });
    return merged;
}

function fillCatalogDefaults(node) {
    let catalogEntry = getBoxCatalogEntry(node.type);
    let filled = {
        ...node,
        properties: mergeWithDefaults(catalogEntry && catalogEntry.properties, node.properties)
    };
    if (catalogEntry && catalogEntry.metadata) {
        filled.metadata = mergeWithDefaults(catalogEntry.metadata, node.metadata);
    }
    if (node.children) {
        filled.children = node.children.map(fillCatalogDefaults);
    }
    return filled;
}

// A rule structure generated by the agent, rendered as a miniaturized tree.
// Each root can be dragged into the rule tree; the button appends every root
// at the end of the rule.
function RuleArtifactCard({ artifact, rulesContext, userVariablesAvailable, onInsertNodes }) {
    const { t } = useTranslation();

    let treeData = (artifact.rule && artifact.rule.tree_data) || [];
    let userVariables = (artifact.rule && artifact.rule.user_variables) || [];
    let unknownTypes = collectUnknownBoxTypes(treeData);
    let insertable = unknownTypes.length === 0;
    let nodeCount = countTreeNodes(treeData);

    // Fresh income translation on every call so repeated drags/inserts never
    // reuse node ids already present in the rule tree.
    const buildIncomeNodes = (outcomeNodes) =>
        translateIncomeTreeData(
            outcomeNodes.map(fillCatalogDefaults),
            rulesContext,
            userVariablesAvailable
        );

    const handleInsertAll = () => {
        if (!insertable) return;
        onInsertNodes(buildIncomeNodes(treeData));
    };

    return (
        <div className="qia-artifact-card">
            <div className="qia-artifact-header">
                <span className="qia-artifact-title">
                    {artifact.title || t("Trecho de regra gerado")}
                </span>
                <span className="qia-artifact-count">
                    {nodeCount + " " + (nodeCount === 1 ? t("nó") : t("nós"))}
                </span>
            </div>
            {insertable ? (
                <React.Fragment>
                    {treeData.map((rootNode, index) => (
                        <DraggableGeneratedRoot
                            key={index}
                            buildIncomeNode={() => buildIncomeNodes([rootNode])[0]}
                        >
                            <MiniRuleTree treeData={[rootNode]} />
                        </DraggableGeneratedRoot>
                    ))}
                    <div className="qia-artifact-footer">
                        <span className="qia-artifact-hint">{t("Arraste para a árvore ou")}</span>
                        <button type="button" className="qia-artifact-insert-btn" onClick={handleInsertAll}>
                            <AddIcon style={{ width: "14px", height: "14px" }} />
                            {t("Adicionar à regra")}
                        </button>
                    </div>
                    {userVariables.length > 0 && (
                        <div className="qia-artifact-variables">
                            {t("Variáveis necessárias:") + " " +
                                userVariables.map((variable) => variable.variable_name).join(", ")}
                        </div>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <MiniRuleTree treeData={treeData} />
                    <div className="qia-artifact-warning">
                        {t("Este trecho contém componentes desconhecidos e não pode ser inserido:") +
                            " " + unknownTypes.join(", ")}
                    </div>
                </React.Fragment>
            )}
        </div>
    );
}

export default RuleArtifactCard;
